'use client';

import * as React from 'react';

import { cn } from '../utils/cn';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, the confirm button uses destructive styling. Defaults to false. */
  destructive?: boolean;
  /** Called when the user confirms the action. */
  onConfirm: () => void | Promise<void>;
  /**
   * Controls when the dialog should close after confirm is clicked.
   * - `afterSuccess` (default): close only if `onConfirm` resolves.
   * - `always`: close regardless of whether `onConfirm` resolves/rejects.
   * - `never`: never close automatically (caller must manage `open`).
   */
  closeBehavior?: 'afterSuccess' | 'always' | 'never';
  /** Optional children rendered between description and footer (e.g. a warning list). */
  children?: React.ReactNode;
  /** Extra className on the dialog content. */
  contentClassName?: string;
}

/**
 * Lightweight confirmation dialog built on top of `AlertDialog`.
 *
 * Use for destructive or otherwise irreversible actions (deletions, disables)
 * where the user should explicitly acknowledge the action.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  destructive = false,
  onConfirm,
  closeBehavior = 'afterSuccess',
  children,
  contentClassName,
}: ConfirmDialogProps) {
  const [isConfirming, setIsConfirming] = React.useState(false);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={cn('max-w-md', contentClassName)}>
        <AlertDialogHeader>
          {/* Long names without spaces wrap instead of widening the
              dialog past the viewport (DEV-820). */}
          <AlertDialogTitle className="[overflow-wrap:anywhere]">
            {title}
          </AlertDialogTitle>
          {description ? (
            <AlertDialogDescription className="[overflow-wrap:anywhere]">
              {description}
            </AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isConfirming}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              // Prevent Radix' default "close on action" so we can reliably close
              // based on `closeBehavior` even when `onConfirm` is async/throws.
              event.preventDefault();
              if (isConfirming) return;

              void (async () => {
                setIsConfirming(true);
                try {
                  await onConfirm();
                  if (closeBehavior === 'afterSuccess') {
                    onOpenChange(false);
                  }
                } catch {
                  // Keep the dialog open on error by default.
                } finally {
                  if (closeBehavior === 'always') {
                    onOpenChange(false);
                  }
                  setIsConfirming(false);
                }
              })();
            }}
            variant={destructive ? 'destructive' : undefined}
            disabled={isConfirming}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
